import React from "react";
import Parse from "parse";
import styled from "styled-components";

import {
  useElementSize,
  useTranslation,
  useEventListener,
  uuid,
} from "@opendash/core";

import { Button, Divider, List, message, Modal, Select } from "antd";

import { GlossaryEntry, Invoice, InvoicePage, Language, Video } from "../types";
import { useParseQuery } from "parse-hooks";

import { DEFAULT_TENANT_ID } from "../const";
import { $parse } from "@opendash/plugin-parse";

const PageImageContainer = styled.div`
  position: relative;

  user-select: none;
`;

const PageImageAnnotation = styled.button`
  position: absolute;
  /* left, top and background are calculated */
  height: 40px;
  width: 40px;
  line-height: 30px;
  font-size: 16px;

  /* color: white;
  background: #1890ff; */

  display: inline-block;
  text-align: center;
  border-radius: 50%;
  // border-top-left-radius: 4px;
  outline: 0;
  border-width: 1px;
  border-style: solid;
`;

const AnnotationListItem = styled.div`
  display: flex;
  width: 100%;
  gap: 10px;

  & ${PageImageAnnotation} {
    width: 30px;
    height: 30px;
    line-height: 24px;
    font-size: 14px;
  }
`;

const INVOICE_WIDTH = 2480;
const INVOICE_HEIGHT = 3508;

const SELECT_FILTER = (input: string, option: any) =>
  option?.label?.toLowerCase().indexOf(input.toLowerCase()) >= 0;

interface Props {
  invoice: Invoice;
  page: InvoicePage | null;
  onClose(): void;
  onChange(objectId: string): void;
}

interface Annotation {
  key: string;
  index: number;
  x: number;
  y: number;
  keyword: string;
  videoId?: string;
}

type XYPosition = [number, number];
type AnnotationIndex = number;

export const InvoicePageAnnotation = React.memo<Props>(
  function InvoicePageAnnotation({ invoice, page, onClose, onChange }) {
    const t = useTranslation();

    const [annotations, setAnnotations] = React.useState<Annotation[]>([]);
    const [reload, setReload] = React.useState<boolean>(false);

    const currentTenantId = Parse.User.current()?.get("tenant")?.id;

    /*
    const glossaryQuery = React.useMemo(
      () =>
        new Parse.Query(GlossaryEntry)
          .equalTo("language", new Language({ id: "de" }))
          .containedIn("tenant", [
            new Parse.Object("OD3_Tenant", { id: currentTenantId }),
            new Parse.Object("OD3_Tenant", { id: DEFAULT_TENANT_ID }),
          ])
          .ascending("title")
          .descending("updatedAt")
          .limit(1000000),
      []
    );

    const glossary = useParseQuery(glossaryQuery);
    */

    const currentTenantQuery = React.useMemo(() => {
      return new Parse.Query(GlossaryEntry)
        .equalTo("language", new Language({ id: "de" }))
        .equalTo(
          "tenant",
          new Parse.Object("OD3_Tenant", { id: currentTenantId })
        )
        .ascending("title")
        .limit(1000000);
    }, [currentTenantId, annotations, reload]);

    const [combinedGlossaryEntries, setCombinedGlossaryEntries] =
      React.useState([]);

    const defaultTenantQuery = React.useMemo(() => {
      return new Parse.Query(GlossaryEntry)
        .equalTo("language", new Language({ id: "de" }))
        .equalTo(
          "tenant",
          new Parse.Object("OD3_Tenant", { id: DEFAULT_TENANT_ID })
        )
        .ascending("title")
        .limit(1000000);
    }, [DEFAULT_TENANT_ID, annotations, reload]);

    React.useEffect(() => {
      const fetchEntries = async () => {
        try {
          // 1. Hol die Einträge vom currentTenantId
          const currentTenantEntries = await currentTenantQuery.find();
          const currentTenantKeywords = currentTenantEntries.map((entry) =>
            entry.get("keyword")
          );

          // 2. Hol die Einträge vom DEFAULT_TENANT_ID, deren Keywords nicht in currentTenantKeywords vorkommen
          const defaultTenantEntries = await defaultTenantQuery
            .notContainedIn("keyword", currentTenantKeywords)
            .find();

          // 3. Kombiniere die Ergebnisse: currentTenantEntries zuerst, dann defaultTenantEntries (falls keyword fehlt)
          setCombinedGlossaryEntries([
            ...currentTenantEntries,
            ...defaultTenantEntries,
          ]);
        } catch (error) {
          console.error("Fehler beim Abrufen der Glossar-Einträge:", error);
        }
      };

      fetchEntries();
    }, [currentTenantQuery, defaultTenantQuery, annotations, reload]);

    const glossaryOptions = React.useMemo(() => {
      const resultMap: Record<string, { label: string; value: string }> = {};

      for (const entry of combinedGlossaryEntries) {
        const title = entry.get("title");
        const tags = entry.get("tags") || [];
        const value = entry.get("keyword");
        const label = tags.length > 0 ? `${title} (${tags.join(", ")})` : title;
        const tenantId = entry.get("tenant")?.id;

        if (
          (resultMap[value] && tenantId === currentTenantId) ||
          !resultMap[value]
        ) {
          resultMap[value] = { label, value };
        }
      }

      const result = Object.values(resultMap).sort((a, b) => {
        return a.label.localeCompare(b.label, "de");
      });

      return result;
    }, [combinedGlossaryEntries]);

    const videoQuery = React.useMemo(
      () =>
        new Parse.Query(Video)
          .equalTo("language", new Language({ id: "de" }))
          .containedIn("tenant", [
            new Parse.Object("OD3_Tenant", { id: DEFAULT_TENANT_ID }),
            new Parse.Object("OD3_Tenant", { id: currentTenantId }),
          ])
          .ascending("title")
          .limit(1000000),
      []
    );

    const videos = useParseQuery(videoQuery);

    const videoOptions = React.useMemo(() => {
      return videos.result.map((video) => ({
        label: video.get("title"),
        value: video.id,
      }));
    }, [videos.result]);

    const [keywordSelect, setKeywordSelect] = React.useState<
      string | undefined
    >();

    const [videoSelect, setVideoSelect] = React.useState<string | undefined>();

    const [addAnnoation, setAddAnnotation] = React.useState<XYPosition | null>(
      null
    );

    const [editAnnoation, setEditAnnotation] =
      React.useState<AnnotationIndex | null>(null);

    const showKeywordModal = addAnnoation !== null || editAnnoation !== null;

    React.useEffect(() => {
      if (!showKeywordModal) {
        setKeywordSelect(undefined);
      }
    }, [showKeywordModal]);

    React.useEffect(() => {
      if (page?.get("annotations")) {
        setAnnotations(page.get("annotations"));
      }
    }, [page?.id]);

    const mouseposition = React.useRef<XYPosition>([0, 0]);
    const annotationDrag = React.useRef<[AnnotationIndex, HTMLElement] | null>(
      null
    );

    const imageHeight = page?.get("imageHeight") || INVOICE_HEIGHT;
    const imageWidth = page?.get("imageWidth") || INVOICE_WIDTH;
    const imageContainerRef = React.useRef<HTMLDivElement>(null);
    const imageContainerSize = useElementSize(imageContainerRef, 1000);
    const imageContainerScale = imageContainerSize.width / imageWidth;

    const calculatePosition = (): XYPosition => {
      if (!imageContainerRef.current) {
        return [0, 0];
      }

      const [mouseX, mouseY] = mouseposition.current;
      const scale = imageContainerScale;
      const maxX = imageWidth;
      const maxY = imageHeight;

      const viewportOffset = imageContainerRef.current.getBoundingClientRect();

      const offsetTop = viewportOffset.top;
      const offsetLeft = viewportOffset.left;

      let x = (mouseX - offsetLeft - 15) / scale;
      let y = (mouseY - offsetTop - 15) / scale;

      x = Math.round(x / 10) * 10;
      y = Math.round(y / 10) * 10;

      x = Math.round(Math.max(0, Math.min(x, maxX)));
      y = Math.round(Math.max(0, Math.min(y, maxY)));

      return [x, y];
    };

    const imageClickHandler = (
      e: React.MouseEvent<HTMLImageElement, MouseEvent>
    ) => {
      const [x, y] = calculatePosition();

      setAddAnnotation([x, y]);
    };

    useEventListener("mousemove", (e: MouseEvent) => {
      mouseposition.current = [e.clientX, e.clientY];

      if (annotationDrag.current !== null) {
        // drag
        const [x, y] = calculatePosition();

        const left = Math.round(x * imageContainerScale);
        const top = Math.round(y * imageContainerScale);

        const [, element] = annotationDrag.current;

        element.style.top = top + "px";
        element.style.left = left + "px";
      }
    });

    useEventListener("mouseup", (e: MouseEvent) => {
      if (annotationDrag.current !== null) {
        // drop

        const [x, y] = calculatePosition();
        const [index] = annotationDrag.current;

        setAnnotations((current) => {
          const next = [...current];

          next[index] = { ...current[index], x, y };

          return sortAnnotation(next);
        });
      }

      annotationDrag.current = null;
    });

    const save = () => {
      if (page) {
        page.set("annotations", annotations);

        page.save().then(
          () => {
            message.success(
              t("app:admin.invoice_page_annotation.save_success")
            );

            onClose();
          },
          () => {
            message.error(t("app:admin.invoice_page_annotation.save_error"));
          }
        );
      }
    };

    return (
      <Modal
        title={t("app:admin.invoice_page_annotation.title")}
        visible={!!page}
        zIndex={998}
        onCancel={() => {
          Modal.confirm({
            title: t("app:admin.invoice_page_annotation.exit_title"),
            content: t("app:admin.invoice_page_annotation.exit_content"),
            onOk: save,
            okText: t("app:admin.invoice_page_annotation.exit_ok"),
            onCancel: onClose,
            cancelText: t("app:admin.invoice_page_annotation.exit_cancel"),
          });
        }}
        width={1000}
        // footer={false}

        okText={t("opendash:ui.save")}
        onOk={save}
      >
        <p>{t("app:admin.invoice_page_annotation.usage")}</p>

        {annotations.length > 0 && (
          <List
            dataSource={annotations}
            rowKey="key"
            renderItem={(annotation) => (
              <List.Item>
                {/* <span>{annotation.index}</span> */}

                <AnnotationListItem>
                  <div>
                    <PageImageAnnotation
                      className="ant-btn-primary"
                      style={{ position: "relative" }}
                      children={annotation.index}
                    />
                  </div>

                  <Select
                    style={{ width: "100%", flexGrow: 1 }}
                    value={annotation.keyword}
                    onChange={(value) => {
                      setAnnotations((current) => {
                        const index = annotations.indexOf(annotation);
                        const next = [...current];

                        next[index] = { ...current[index], keyword: value };

                        return sortAnnotation(next);
                      });
                    }}
                    options={glossaryOptions}
                    showSearch
                    filterOption={SELECT_FILTER}
                  />

                  <Button
                    danger
                    children={t(
                      "app:admin.invoice_page_annotation.keyword_modal_delete"
                    )}
                    onClick={() => {
                      setAnnotations((current) => {
                        const index = annotations.indexOf(annotation);
                        const next = [...current];

                        next.splice(index, 1);

                        return sortAnnotation(next);
                      });

                      setEditAnnotation(null);
                    }}
                  />
                </AnnotationListItem>
              </List.Item>
            )}
          />
        )}

        <Divider />

        {page && (
          <PageImageContainer ref={imageContainerRef}>
            <img
              src={page.get("image")?.url()}
              style={{
                width: imageContainerSize.width,
                height: Math.round(imageHeight * imageContainerScale),
                border: "1px solid #d9d9d9",
              }}
              onClick={imageClickHandler}
            />

            {annotations.map((annotation, index) => {
              const left = Math.round(annotation.x * imageContainerScale);
              const top = Math.round(annotation.y * imageContainerScale);

              return (
                <PageImageAnnotation
                  className="ant-btn-primary"
                  title={combinedGlossaryEntries
                    .find((e) => e.get("keyword") === annotation.keyword)
                    ?.get("title")}
                  key={annotation.key}
                  style={{ left, top }}
                  onClick={(e) => {
                    if (e.detail === 2) {
                      setEditAnnotation(index);
                      setKeywordSelect(annotation.keyword);
                      setVideoSelect(annotation.videoId);
                    }
                  }}
                  onMouseDown={(e) => {
                    // drag start
                    annotationDrag.current = [index, e.target as HTMLElement];
                  }}
                >
                  {annotation.index}
                </PageImageAnnotation>
              );
            })}
          </PageImageContainer>
        )}

        <Modal
          title={t("app:admin.invoice_page_annotation.keyword_modal_title")}
          visible={showKeywordModal}
          zIndex={999}
          onCancel={() => {
            setEditAnnotation(null);
            setAddAnnotation(null);
            setVideoSelect(undefined);
          }}
          onOk={() => {
            if (keywordSelect) {
              if (addAnnoation !== null) {
                const [x, y] = addAnnoation;

                const key = uuid();

                setAnnotations((current) => {
                  const next = [
                    ...current,
                    {
                      key,
                      index: 0,
                      x,
                      y,
                      keyword: keywordSelect,
                      videoId: videoSelect,
                    },
                  ];

                  return sortAnnotation(next);
                });
              }

              if (editAnnoation !== null) {
                const index = editAnnoation;

                setAnnotations((current) => {
                  const next = [...current];

                  next[index] = {
                    ...current[index],
                    keyword: keywordSelect,
                    videoId: videoSelect,
                  };

                  return sortAnnotation(next);
                });
              }
            }

            setEditAnnotation(null);
            setAddAnnotation(null);
            setVideoSelect(undefined);
          }}
          okText={t("opendash:ui.save")}
          okButtonProps={{
            disabled: !keywordSelect,
          }}
        >
          <Select
            autoFocus
            value={keywordSelect}
            onChange={(value) => {
              setKeywordSelect(value);
            }}
            options={glossaryOptions}
            style={{ width: "100%" }}
            showSearch
            filterOption={SELECT_FILTER}
          />

          {keywordSelect && (
            <div style={{ paddingTop: 20 }}>
              {combinedGlossaryEntries
                .find((e) => e.get("keyword") === keywordSelect)
                ?.get("text")}

              <Button
                type="link"
                onClick={() => {
                  const entries = combinedGlossaryEntries.filter(
                    (e) => e.get("keyword") === keywordSelect
                  );

                  if (entries.length === 2) {
                    const e = entries.find(
                      (e) => e.get("tenant")?.id === currentTenantId
                    );

                    if (e) {
                      $parse.ui.updateObject(e.className, e.id).then(() => {
                        setReload(!reload);
                      });
                    }
                  }
                  if (entries.length === 1) {
                    const e = entries[0];

                    if (e.get("tenant")?.id === currentTenantId) {
                      $parse.ui.updateObject(e.className, e.id).then(() => {
                        setReload(!reload);
                      });
                    } else {
                      $parse.ui.copyObject(e.className, e.id).then(() => {
                        setReload(!reload);
                      });
                    }
                  }
                }}
              >
                {t("opendash:ui.edit")}
              </Button>
            </div>
          )}

          <Divider />

          <p
            children={t("app:admin.invoice_page_annotation.video_description")}
          />

          <Select
            placeholder={t(
              "app:admin.invoice_page_annotation.video_placeholder"
            )}
            allowClear
            value={videoSelect}
            onChange={(value) => {
              setVideoSelect(value);
            }}
            options={videoOptions}
            style={{ width: "100%" }}
          />

          {editAnnoation !== null && (
            <>
              <Divider />
              <Button
                danger
                children={t(
                  "app:admin.invoice_page_annotation.keyword_modal_delete"
                )}
                onClick={() => {
                  setAnnotations((current) => {
                    const next = [...current];

                    next.splice(editAnnoation, 1);

                    return sortAnnotation(next);
                  });

                  setEditAnnotation(null);
                }}
              />
            </>
          )}
        </Modal>
      </Modal>
    );
  }
);

function sortAnnotation(annoations: Annotation[]) {
  const result = [...annoations].sort((a, b) => {
    if (a.y > b.y) {
      return 1;
    }

    if (a.y < b.y) {
      return -1;
    }

    if (a.x > b.x) {
      return 1;
    }

    if (a.x < b.x) {
      return -1;
    }

    return 0;
  });

  // set index

  for (const annoation of result) {
    annoation.index = 0;
  }

  let index = 1;

  for (const annoation of result) {
    annoation.index =
      result.find((a) => a.keyword === annoation.keyword)?.index || index++;
  }

  return result;
}
